import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkle, X, Moon } from "@phosphor-icons/react";
import { authFetch } from "../context/AuthContext";
import { fileUrl } from "./PhotoGallery";

const SIGN_GLYPH = {
    Aries: "\u2648", Taurus: "\u2649", Gemini: "\u264A", Cancer: "\u264B",
    Leo: "\u264C", Virgo: "\u264D", Libra: "\u264E", Scorpio: "\u264F",
    Sagittarius: "\u2650", Capricorn: "\u2651", Aquarius: "\u2652", Pisces: "\u2653",
};

const DISMISS_KEY = "soulstar_weekly_digest_dismissed";

export default function WeeklyDigest() {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        authFetch("get", "/digest/weekly")
            .then((r) => {
                setData(r.data);
                const stored = localStorage.getItem(DISMISS_KEY);
                if (stored === r.data.week) setDismissed(true);
            })
            .catch(() => {});
    }, []);

    const dismiss = () => {
        if (data?.week) localStorage.setItem(DISMISS_KEY, data.week);
        setDismissed(true);
    };

    if (!data || dismissed || !data.top_matches?.length) return null;

    const nextShift = data.next_moon_shift;
    const daysAway = nextShift?.days_away ?? 0;
    const shiftLabel = daysAway < 1
        ? "later today"
        : daysAway < 2
            ? "tomorrow"
            : `in ${Math.round(daysAway)} days`;

    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-gold ai-card p-6 sm:p-7 relative"
            data-testid="weekly-digest"
        >
            <button
                onClick={dismiss}
                className="absolute top-3 right-3 w-7 h-7 rounded-full border border-white/10 text-muted hover:text-gold hover:border-gold/40 flex items-center justify-center transition-colors"
                aria-label="Dismiss this week"
                data-testid="weekly-digest-dismiss"
            >
                <X size={12} />
            </button>

            <div className="mb-4">
                <p className="label-tracking text-gold flex items-center gap-2">
                    <Sparkle size={12} weight="duotone" /> Weekly Cosmic Digest · {data.week}
                </p>
                <h2 className="font-serif text-2xl sm:text-3xl mt-1">Your week in the stars</h2>
            </div>

            {/* Moon shift banner */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 mb-5" data-testid="weekly-digest-moon">
                <div className="w-10 h-10 rounded-full bg-[#1a1a2e] border border-gold/30 flex items-center justify-center flex-shrink-0">
                    <Moon size={16} weight="duotone" className="text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs label-tracking text-gold">Upcoming Moon Shift</p>
                    <p className="font-serif text-lg leading-tight truncate">
                        {nextShift?.emoji} {nextShift?.phase_name}
                        <span className="text-muted text-sm ml-2">{shiftLabel}</span>
                    </p>
                </div>
            </div>

            {/* Top 3 matches */}
            <p className="label-tracking text-muted mb-3">Top three alignments this week</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {data.top_matches.map((m, i) => (
                    <Link
                        key={m.user_id}
                        to={`/u/${m.user_id}`}
                        className="glass p-3 hover-lift block group"
                        data-testid={`weekly-match-${i}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-white/5 border border-gold/20 overflow-hidden flex items-center justify-center flex-shrink-0">
                                {m.photos?.[0] ? (
                                    <img src={fileUrl(m.photos[0].storage_path)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-xl text-gold font-serif">{SIGN_GLYPH[m.sun]}</span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-serif text-base truncate group-hover:text-gold transition-colors">
                                    {m.name}{m.age ? `, ${m.age}` : ""}
                                </p>
                                <p className="text-[10px] text-muted truncate">
                                    {SIGN_GLYPH[m.sun]} {m.sun}
                                </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <p className="font-serif text-2xl text-gold leading-none">{m.compatibility.overall}</p>
                                <p className="label-tracking text-[9px] text-muted mt-0.5">score</p>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </motion.section>
    );
}
